import React from 'react';
import { HIGHLIGHTERS } from '../../lib/paper';
import { calendarFace } from '../../lib/desk';
import { Tape } from '../paper';

/**
 * A tear-off desk calendar page for the Home masthead (mobile's DeskCalendar):
 * coral month band with a perforated edge, today's date big below, taped on and
 * askew. Clicking it narrows the inbox to today's pages (`active` while on).
 */
export const DeskCalendar: React.FC<{ date: Date; active?: boolean; onClick?: () => void }> = ({
  date,
  active = false,
  onClick,
}) => {
  const face = calendarFace(date);
  const label = date.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' });
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!onClick}
      aria-pressed={onClick ? active : undefined}
      aria-label={onClick ? `${label}. ${active ? 'Show all pages' : "Show today's pages"}` : label}
      className={`pb-squish relative flex w-16 shrink-0 cursor-pointer flex-col items-center rounded-lg rounded-br-none bg-surface-container-lowest p-0 pb-2 font-body shadow-[0_12px_40px_rgba(28,27,25,0.12)] disabled:cursor-default ${
        active ? 'border-[1.5px] border-solid border-hl-mint-ink' : 'border border-solid border-outline-variant'
      }`}
      style={{ '--tilt': '-4deg' } as React.CSSProperties}
    >
      <span className="block self-stretch rounded-t-[7px] bg-hl-coral pb-1 pt-2 text-center text-[10px] font-bold leading-[13px] tracking-[0.16em] text-hl-coral-ink">
        {face.month}
      </span>
      {/* Tear line between the band and the page. */}
      <span className="-mt-0.5 flex self-stretch justify-evenly" aria-hidden>
        {Array.from({ length: 7 }, (_, i) => (
          <span key={i} className="h-1 w-1 rounded-full bg-surface" />
        ))}
      </span>
      <span className="mt-0.5 font-headline text-[30px] font-medium leading-[34px] text-on-surface">{face.day}</span>
      <span className="text-[9.5px] font-semibold leading-3 tracking-[0.15em] text-on-surface-variant">
        {face.weekday}
      </span>
      {/* Dog-eared corner: the folded flap over desk showing through. */}
      <span
        aria-hidden
        className="absolute -bottom-px -right-px h-0 w-0 border-r-[12px] border-t-[12px] border-solid border-r-surface border-t-surface-container-high"
      />
      <Tape tone={HIGHLIGHTERS.yellow} width={34} height={12} rotate={-6} className="left-1/2 top-[-7px] -ml-[17px]" />
    </button>
  );
};
